import instance from 'services/api';
import { deepCloneFloorPlan } from './floorPlanStorage';

const asList = (value) => {
  if (Array.isArray(value)) {
    return value;
  }
  if (Array.isArray(value?.data)) {
    return value.data;
  }
  return [];
};

const readUuid = (entity, detailsKey) => {
  const details = entity?.[detailsKey];
  return (
    (typeof details?.uuid === 'string' && details.uuid) ||
    (typeof entity?.uuid === 'string' && entity.uuid) ||
    null
  );
};

const readSectionLabel = (section) => {
  const details = section?.sectionDetails || section || {};
  return (
    (typeof details.displayName === 'string' && details.displayName.trim()) ||
    (typeof details.name === 'string' && details.name.trim()) ||
    ''
  );
};

const readTableLabel = (table) => {
  const details = table?.tableDetails || table || {};
  return (
    (typeof details.displayName === 'string' && details.displayName.trim()) ||
    (typeof details.name === 'string' && details.name.trim()) ||
    ''
  );
};

const normalizeLabel = (value) =>
  typeof value === 'string' ? value.trim().toLowerCase() : '';

/**
 * Carry the partially-synced plan on the error so a retry sees the rows that
 * were already created (under their real UUIDs) instead of creating duplicates.
 */
const attachPartialPlan = (err, plan) => {
  if (err && typeof err === 'object') {
    try {
      err.partialPlan = plan;
    } catch {
      // non-extensible error — retry idempotency is best-effort
    }
  }
  return err;
};

/** Best available human message from an API error. */
export const readApiErrorMessage = (err) => {
  const payload = err?.response?.data;
  const message = payload?.message;
  if (typeof message === 'string' && message.trim()) {
    return message.trim();
  }
  if (typeof message?.message === 'string' && message.message.trim()) {
    return message.message.trim();
  }
  if (typeof payload?.error === 'string' && payload.error.trim()) {
    return payload.error.trim();
  }
  return typeof err?.message === 'string' ? err.message : '';
};

const extractCreatedUuid = (payload, detailsKey) => {
  if (!payload || typeof payload !== 'object') {
    return null;
  }
  return (
    readUuid(payload, detailsKey) ||
    readUuid(payload?.data, detailsKey) ||
    (typeof payload?.[detailsKey]?.uuid === 'string'
      ? payload[detailsKey].uuid
      : null)
  );
};

const fetchSectionsCatalog = async (businessUuid) => {
  const sectionsRes = await instance.get('/cloud-pos/section', {
    headers: { businessUuid },
  });
  return asList(sectionsRes?.data?.data ?? sectionsRes?.data);
};

const fetchTablesCatalog = async (businessUuid) => {
  const tablesRes = await instance.get('/cloud-pos/table', {
    headers: { businessUuid },
  });
  return asList(tablesRes?.data?.data ?? tablesRes?.data);
};

const fetchCatalog = async (businessUuid, { includeTables = true } = {}) => {
  if (!includeTables) {
    return {
      catalogSections: await fetchSectionsCatalog(businessUuid),
      catalogTables: [],
    };
  }
  const [catalogSections, catalogTables] = await Promise.all([
    fetchSectionsCatalog(businessUuid),
    fetchTablesCatalog(businessUuid),
  ]);
  return { catalogSections, catalogTables };
};

const collectSectionUuids = (plan) =>
  new Set(Object.keys(plan?.sections || {}));

const hasRemovedSections = (previousPlan, nextPlan) => {
  const next = collectSectionUuids(nextPlan);
  for (const uuid of collectSectionUuids(previousPlan)) {
    if (!next.has(uuid)) {
      return true;
    }
  }
  return false;
};

/** uuid → section|name|capacity — used to detect real table changes. */
const collectTableMeta = (plan) => {
  const meta = new Map();
  Object.entries(plan?.sections || {}).forEach(([sectionUuid, layout]) => {
    Object.entries(layout?.tables || {}).forEach(([tableUuid, placement]) => {
      const name =
        (typeof placement?.name === 'string' && placement.name.trim()) ||
        (typeof placement?.displayName === 'string' &&
          placement.displayName.trim()) ||
        '';
      const capacity =
        typeof placement?.capacity === 'number' &&
        Number.isFinite(placement.capacity)
          ? placement.capacity
          : '';
      meta.set(tableUuid, `${sectionUuid}|${name}|${capacity}`);
    });
  });
  return meta;
};

const tablesChanged = (previousPlan, nextPlan) => {
  const prev = collectTableMeta(previousPlan);
  const next = collectTableMeta(nextPlan);
  if (prev.size !== next.size) {
    return true;
  }
  for (const [uuid, key] of prev) {
    if (next.get(uuid) !== key) {
      return true;
    }
  }
  for (const uuid of next.keys()) {
    if (!prev.has(uuid)) {
      return true;
    }
  }
  return false;
};

/**
 * Partner floor-plan Save writes `table_visualization`, but POS Dine-In also
 * needs real `/cloud-pos/section` + `/cloud-pos/table` rows.
 *
 * Create only when the entity is truly missing. Prefer UUID match; if the API
 * ignored client UUIDs on a prior create, fall back to name (+ section) match
 * and remap the plan key so later Saves do not POST again.
 *
 * Returns `{ plan, touchedSections, touchedTables }` with any UUID remaps applied.
 * `touchedTables` is false when the plan (and previous plan) have no tables, so
 * callers can skip `/cloud-pos/table` list refreshes after a section-only Save.
 */
export const syncFloorPlanCatalogWithPlan = async ({
  businessUuid,
  plan,
  previousPlan,
  deletedTableUuids = [],
  deletedSectionUuids = [],
  catalogSections: catalogSectionsArg,
  catalogTables: catalogTablesArg,
}) => {
  if (!businessUuid) {
    throw new Error('businessUuid is required to sync floor plan catalog');
  }

  const nextPlan = deepCloneFloorPlan(plan);
  // Touch /cloud-pos/table only when tables were added/removed/renamed/moved,
  // a section was removed, or something was explicitly deleted.
  // Adding an empty section alone must NOT call table APIs.
  const needsTableSync =
    tablesChanged(previousPlan, nextPlan) ||
    hasRemovedSections(previousPlan, nextPlan) ||
    asList(deletedTableUuids).length > 0 ||
    asList(deletedSectionUuids).length > 0;
  let touchedSections = false;
  let touchedTables = false;

  const previousTableUuids = new Set();
  Object.values(previousPlan?.sections || {}).forEach((sectionLayout) => {
    Object.keys(sectionLayout?.tables || {}).forEach((tableUuid) => {
      previousTableUuids.add(tableUuid);
    });
  });

  // Always re-read catalog at sync time so a prior Save is visible even if the
  // caller passed a stale Redux snapshot. Skip table GET when neither the
  // current nor previous plan contains placements (section-only Save).
  let catalogSections = asList(catalogSectionsArg);
  let catalogTables = asList(catalogTablesArg);
  let tablesCatalogLoaded = !needsTableSync;
  try {
    const fresh = await fetchCatalog(businessUuid, {
      includeTables: needsTableSync,
    });
    catalogSections = fresh.catalogSections;
    catalogTables = fresh.catalogTables;
    tablesCatalogLoaded = true;
  } catch {
    // Fall back to caller-provided lists if refresh fails.
    tablesCatalogLoaded =
      asList(catalogTablesArg).length > 0 || !needsTableSync;
  }

  const sectionByUuid = new Map();
  const unusedSections = new Set();
  catalogSections.forEach((section) => {
    const uuid = readUuid(section, 'sectionDetails');
    if (!uuid) {
      return;
    }
    sectionByUuid.set(uuid, section);
    unusedSections.add(uuid);
  });

  const tableByUuid = new Map();
  const unusedTables = new Set();
  catalogTables.forEach((table) => {
    const uuid = readUuid(table, 'tableDetails');
    if (!uuid) {
      return;
    }
    tableByUuid.set(uuid, table);
    unusedTables.add(uuid);
  });

  const findSectionByName = (name) => {
    const target = normalizeLabel(name);
    if (!target) {
      return null;
    }
    for (const uuid of unusedSections) {
      const section = sectionByUuid.get(uuid);
      if (normalizeLabel(readSectionLabel(section)) === target) {
        return { uuid, section };
      }
    }
    return null;
  };

  const findTableByName = (name, sectionUuid) => {
    const target = normalizeLabel(name);
    if (!target || !sectionUuid) {
      return null;
    }
    for (const uuid of unusedTables) {
      const table = tableByUuid.get(uuid);
      const details = table?.tableDetails || {};
      // Require same section — never bind a table from another section.
      if (details.sectionUuid !== sectionUuid) {
        continue;
      }
      if (normalizeLabel(readTableLabel(table)) === target) {
        return { uuid, table };
      }
    }
    return null;
  };

  const mergeCatalogSections = (list) => {
    list.forEach((section) => {
      const uuid = readUuid(section, 'sectionDetails');
      if (!uuid) {
        return;
      }
      if (!sectionByUuid.has(uuid)) {
        unusedSections.add(uuid);
      }
      sectionByUuid.set(uuid, section);
    });
  };

  const mergeCatalogTables = (list) => {
    list.forEach((table) => {
      const uuid = readUuid(table, 'tableDetails');
      if (!uuid) {
        return;
      }
      if (!tableByUuid.has(uuid)) {
        unusedTables.add(uuid);
      }
      tableByUuid.set(uuid, table);
    });
  };

  const bindCreatedSection = async (clientUuid, name, createdPayload) => {
    let createdUuid = extractCreatedUuid(createdPayload, 'sectionDetails');
    if (createdUuid && createdUuid !== clientUuid) {
      const remapped = remapSectionUuid(clientUuid, createdUuid);
      const createdEntity = createdPayload?.sectionDetails
        ? createdPayload
        : { sectionDetails: { uuid: createdUuid, name } };
      sectionByUuid.set(createdUuid, createdEntity);
      claimSection(createdUuid);
      return remapped;
    }

    // API may ignore client uuid / omit it in the response — verify via GET.
    try {
      mergeCatalogSections(await fetchSectionsCatalog(businessUuid));
    } catch {
      // keep local maps
    }

    if (sectionByUuid.has(clientUuid)) {
      claimSection(clientUuid);
      return clientUuid;
    }

    const byName = findSectionByName(name);
    if (byName) {
      const remapped = remapSectionUuid(clientUuid, byName.uuid);
      claimSection(byName.uuid);
      return remapped;
    }

    // Last resort: keep client uuid (server may have accepted it).
    createdUuid = createdUuid || clientUuid;
    sectionByUuid.set(createdUuid, {
      sectionDetails: { uuid: createdUuid, name },
    });
    claimSection(createdUuid);
    return remapSectionUuid(clientUuid, createdUuid);
  };

  const bindCreatedTable = async (
    layout,
    clientUuid,
    sectionUuid,
    name,
    capacity,
    createdPayload
  ) => {
    // Prefer explicit create response UUID so bulk creates (T1, T2, T3…) each
    // remap to their own server row instead of name-colliding.
    let createdUuid =
      extractCreatedUuid(createdPayload, 'tableDetails') ||
      (typeof createdPayload?.tableDetails?.uuid === 'string'
        ? createdPayload.tableDetails.uuid
        : null) ||
      (typeof createdPayload?.uuid === 'string' ? createdPayload.uuid : null);

    if (createdUuid) {
      const remapped = remapTableUuid(layout, clientUuid, createdUuid);
      const createdEntity = createdPayload?.tableDetails
        ? createdPayload
        : {
            tableDetails: {
              uuid: createdUuid,
              sectionUuid,
              name,
              displayName: name,
              capacity,
            },
          };
      tableByUuid.set(createdUuid, createdEntity);
      claimTable(createdUuid);
      return remapped;
    }

    try {
      mergeCatalogTables(await fetchTablesCatalog(businessUuid));
    } catch {
      // keep local maps
    }

    if (tableByUuid.has(clientUuid)) {
      claimTable(clientUuid);
      return clientUuid;
    }

    const byName = findTableByName(name, sectionUuid);
    if (byName) {
      const remapped = remapTableUuid(layout, clientUuid, byName.uuid);
      claimTable(byName.uuid);
      return remapped;
    }

    // Keep client key in the plan; next Save can reconcile if catalog lags.
    tableByUuid.set(clientUuid, {
      tableDetails: {
        uuid: clientUuid,
        sectionUuid,
        name,
        displayName: name,
        capacity,
      },
    });
    claimTable(clientUuid);
    return clientUuid;
  };

  const claimSection = (uuid) => {
    unusedSections.delete(uuid);
  };

  const claimTable = (uuid) => {
    unusedTables.delete(uuid);
  };

  const remapSectionUuid = (fromUuid, toUuid) => {
    if (!fromUuid || !toUuid || fromUuid === toUuid) {
      return toUuid || fromUuid;
    }
    if (nextPlan.sections[fromUuid]) {
      nextPlan.sections[toUuid] = nextPlan.sections[fromUuid];
      delete nextPlan.sections[fromUuid];
    }
    return toUuid;
  };

  const remapTableUuid = (layout, fromUuid, toUuid) => {
    if (!layout?.tables || !fromUuid || !toUuid || fromUuid === toUuid) {
      return toUuid || fromUuid;
    }
    if (layout.tables[fromUuid]) {
      layout.tables[toUuid] = layout.tables[fromUuid];
      delete layout.tables[fromUuid];
    }
    return toUuid;
  };

  // ── Sections: create / rename (never recreate existing) ─────────────────
  for (const originalSectionUuid of Object.keys(nextPlan.sections || {})) {
    let sectionUuid = originalSectionUuid;
    const layout = nextPlan.sections[sectionUuid];
    if (!layout) {
      continue;
    }

    const name =
      (typeof layout?.name === 'string' && layout.name.trim()) ||
      (typeof layout?.displayName === 'string' && layout.displayName.trim()) ||
      'Section';
    const displayName =
      (typeof layout?.displayName === 'string' && layout.displayName.trim()) ||
      name;

    nextPlan.sections[sectionUuid] = {
      ...layout,
      name,
      displayName,
    };

    let existing = sectionByUuid.get(sectionUuid);
    if (existing) {
      claimSection(sectionUuid);
    } else {
      const byName = findSectionByName(name);
      if (byName) {
        sectionUuid = remapSectionUuid(sectionUuid, byName.uuid);
        existing = byName.section;
        claimSection(byName.uuid);
      }
    }

    if (!existing) {
      try {
        // Match Tables/Sections UI create body — do not send client `uuid`
        // (API rejects/ignores it and leaves plan/catalog out of sync).
        const response = await instance.post('/cloud-pos/section', {
          businessUuid,
          name,
          displayName,
        });
        const created = response?.data?.data ?? response?.data;
        sectionUuid = await bindCreatedSection(sectionUuid, name, created);
        touchedSections = true;
      } catch (err) {
        // Likely already exists (e.g. prior Save). Re-read and bind by name.
        try {
          mergeCatalogSections(await fetchSectionsCatalog(businessUuid));
        } catch {
          // ignore refresh errors; fall through to name match / rethrow
        }
        const byName = findSectionByName(name);
        if (!byName) {
          throw attachPartialPlan(err, nextPlan);
        }
        sectionUuid = remapSectionUuid(sectionUuid, byName.uuid);
        existing = byName.section;
        claimSection(byName.uuid);
      }
    }

    if (existing) {
      const curName = existing.sectionDetails?.name;
      const curDisplay = existing.sectionDetails?.displayName;
      if (curName !== name || curDisplay !== displayName) {
        await instance.patch(`/cloud-pos/section/${sectionUuid}`, {
          businessUuid,
          name,
          displayName,
        });
        touchedSections = true;
      }
    } else {
      touchedSections = true;
    }
  }

  // ── Tables: create / update identity (never recreate existing) ──────────
  // Section keys are already remapped to catalog UUIDs above. Use those keys
  // as sectionUuid on create so bulk-added tables land in the right section.
  for (const [sectionUuid, layout] of Object.entries(
    needsTableSync ? nextPlan.sections || {} : {}
  )) {
    if (!sectionByUuid.has(sectionUuid)) {
      // Section failed to sync — do not create orphan tables under a fake id.
      continue;
    }

    const tableEntries = Object.entries(layout.tables || {});
    for (const [originalTableUuid, placement] of tableEntries) {
      let tableUuid = originalTableUuid;
      if (!layout.tables[tableUuid]) {
        continue;
      }

      const isNewlyAdded = !previousTableUuids.has(originalTableUuid);
      const name =
        (typeof placement?.name === 'string' && placement.name.trim()) ||
        (typeof placement?.displayName === 'string' &&
          placement.displayName.trim()) ||
        '';
      const displayName =
        (typeof placement?.displayName === 'string' &&
          placement.displayName.trim()) ||
        name;
      const capacity = Math.max(
        1,
        typeof placement?.capacity === 'number' &&
          Number.isFinite(placement.capacity)
          ? Math.round(placement.capacity)
          : 4
      );

      // Bulk add always embeds name/capacity. Skip broken placements.
      if (!name) {
        continue;
      }

      layout.tables[tableUuid] = {
        ...placement,
        name,
        displayName: displayName || name,
        capacity,
      };

      let existing = tableByUuid.get(tableUuid);
      if (existing) {
        claimTable(tableUuid);
      } else if (!isNewlyAdded) {
        // Only bind legacy viz-only rows by name. Newly added bulk tables must
        // POST create — name-matching would steal older catalog rows (T1, T2…).
        const byName = findTableByName(name, sectionUuid);
        if (byName) {
          tableUuid = remapTableUuid(layout, tableUuid, byName.uuid);
          existing = byName.table;
          claimTable(byName.uuid);
        }
      }

      if (!existing) {
        if (!isNewlyAdded && !tablesCatalogLoaded) {
          continue;
        }
        try {
          // CreateTableRequestDto: businessUuid, sectionUuid?, name,
          // displayName?, capacity. Never send client uuid.
          const body = {
            businessUuid,
            sectionUuid,
            name,
            displayName: displayName || name,
            capacity,
          };
          const response = await instance.post('/cloud-pos/table', body);
          const created = response?.data?.data ?? response?.data;
          tableUuid = await bindCreatedTable(
            layout,
            tableUuid,
            sectionUuid,
            name,
            capacity,
            created
          );
          touchedTables = true;
        } catch (err) {
          try {
            mergeCatalogTables(await fetchTablesCatalog(businessUuid));
            tablesCatalogLoaded = true;
          } catch {
            // ignore
          }
          const byName = findTableByName(name, sectionUuid);
          if (byName) {
            tableUuid = remapTableUuid(layout, tableUuid, byName.uuid);
            existing = byName.table;
            claimTable(byName.uuid);
          } else if (isNewlyAdded) {
            throw attachPartialPlan(err, nextPlan);
          }
        }
      }

      if (existing) {
        const details = existing.tableDetails || {};
        const needsPatch =
          details.name !== name ||
          (details.displayName || '') !== (displayName || name) ||
          Number(details.capacity) !== capacity ||
          details.sectionUuid !== sectionUuid;
        if (needsPatch) {
          await instance.patch(`/cloud-pos/table/${tableUuid}`, {
            businessUuid,
            sectionUuid,
            name,
            displayName: displayName || name,
            capacity,
          });
          touchedTables = true;
        }
      }
    }
  }

  // ── Deletes: only what the user explicitly deleted ──────────────────────
  // "Remove from floor" merely drops the placement, so plan membership is NOT
  // a delete signal — otherwise unplacing a table would destroy the real row.
  const nextTableUuids = new Set();
  const nextSectionUuids = new Set(Object.keys(nextPlan.sections || {}));
  Object.values(nextPlan.sections || {}).forEach((sectionLayout) => {
    Object.keys(sectionLayout?.tables || {}).forEach((tableUuid) => {
      nextTableUuids.add(tableUuid);
    });
  });

  // A removed item only leaves the visualization once the real row is gone.
  // If the API refuses (e.g. an occupied table), put it back in the plan so the
  // PATCH keeps it on the floor and the caller can report why.
  const failedDeletes = [];

  const previousTableInfo = new Map();
  Object.entries(previousPlan?.sections || {}).forEach(
    ([sectionUuid, sectionLayout]) => {
      Object.entries(sectionLayout?.tables || {}).forEach(
        ([tableUuid, placement]) => {
          previousTableInfo.set(tableUuid, { sectionUuid, placement });
        }
      );
    }
  );

  const restoreSection = (sectionUuid) => {
    const previousSection = previousPlan?.sections?.[sectionUuid];
    if (!previousSection || nextPlan.sections[sectionUuid]) {
      nextSectionUuids.add(sectionUuid);
      return;
    }
    nextPlan.sections[sectionUuid] = {
      ...deepCloneFloorPlan(previousSection),
      tables: {},
    };
    nextSectionUuids.add(sectionUuid);
  };

  const restoreTable = (tableUuid) => {
    const info = previousTableInfo.get(tableUuid);
    if (!info) {
      return;
    }
    restoreSection(info.sectionUuid);
    const layout = nextPlan.sections[info.sectionUuid];
    if (!layout) {
      return;
    }
    layout.tables = layout.tables || {};
    layout.tables[tableUuid] = deepCloneFloorPlan(info.placement);
    nextTableUuids.add(tableUuid);
  };

  const recordFailedDelete = (type, uuid, label, err) => {
    failedDeletes.push({
      type,
      uuid,
      label: label || uuid,
      message: readApiErrorMessage(err),
    });
  };

  const sectionsToDelete = new Set(asList(deletedSectionUuids));
  const tablesToDelete = new Set(asList(deletedTableUuids));

  // Cascade: a deleted section takes its real tables with it.
  if (sectionsToDelete.size) {
    for (const [tableUuid, table] of tableByUuid) {
      const details = table?.tableDetails || {};
      if (details.sectionUuid && sectionsToDelete.has(details.sectionUuid)) {
        tablesToDelete.add(tableUuid);
      }
    }
  }

  const sectionsBlockedByTables = new Set();
  for (const tableUuid of tablesToDelete) {
    const catalogTable = tableByUuid.get(tableUuid);
    if (!catalogTable) {
      // Never created (draft) or already gone — nothing to delete.
      continue;
    }
    try {
      await instance.delete(`/cloud-pos/table/${tableUuid}`);
      tableByUuid.delete(tableUuid);
      unusedTables.delete(tableUuid);
      touchedTables = true;
    } catch (err) {
      const details = catalogTable?.tableDetails || {};
      if (details.sectionUuid) {
        sectionsBlockedByTables.add(details.sectionUuid);
      }
      restoreTable(tableUuid);
      recordFailedDelete('table', tableUuid, readTableLabel(catalogTable), err);
    }
  }

  for (const sectionUuid of sectionsToDelete) {
    const catalogSection = sectionByUuid.get(sectionUuid);
    if (!catalogSection) {
      continue;
    }

    // Do not attempt to drop a section that still holds undeletable tables.
    if (sectionsBlockedByTables.has(sectionUuid)) {
      restoreSection(sectionUuid);
      continue;
    }

    try {
      await instance.delete(`/cloud-pos/section/${sectionUuid}`);
      sectionByUuid.delete(sectionUuid);
      unusedSections.delete(sectionUuid);
      touchedSections = true;
    } catch (err) {
      restoreSection(sectionUuid);
      recordFailedDelete(
        'section',
        sectionUuid,
        readSectionLabel(catalogSection),
        err
      );
    }
  }

  return {
    plan: nextPlan,
    touchedSections,
    touchedTables,
    failedDeletes,
  };
};
